<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBSite from '@/types/site'
import { notifyError, emptyMongoId } from '@/utils/helpers'

const display = useDisplay()

const isLoading = ref(false)
const filter = ref('')
const sites = ref<MDBSite[]>([])

const filteredSites = computed(() => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return sites.value
  return sites.value.filter(s =>
    s.nom.toLowerCase().includes(q) ||
    s.lettresDappel.toLowerCase().includes(q) ||
    s.type.toLowerCase().includes(q))
})

const headers: DataTableHeaders = [
  { title: 'Lettres d\'appel', key: 'lettresDappel', width: '110px' },
  { title: 'Nom', key: 'nom', width: '180px' },
  { title: 'Type', key: 'type', width: '110px' },
  { title: 'Groupe', key: 'siteGroupeLD', width: '90px' },
  { title: 'Membre Prisme', key: 'estMembrePrisme', width: '130px' },
  { title: 'URL', key: 'url' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'end', width: '80px' },
]

async function getData() {
  isLoading.value = true
  try {
    const res = await apiProphet.get('sites')
    sites.value = res.data as MDBSite[]
  } catch (e) {
    notifyError(e)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  getData()
})

// ── Dialog ────────────────────────────────────────────────────────────────
const emptyForm = (): MDBSite => ({
  id: '',
  nom: '',
  lettresDappel: '',
  lettresDappelPrisme: '',
  type: '',
  siteGroupeLD: '',
  estMembrePrisme: false,
  url: '',
  latitudeGoogleMaps: 0,
  longitudeGoogleMaps: 0,
  latitudeSourceParDéfaut: 0,
  longitudeSourceParDéfaut: 0,
  baseDegréJourParDéfaut: 0,
  sourceDegréJourParDéfaut: '',
  accès: [],
  accèsNon: [],
  farmit: null,
})

const showDialog = ref(false)
const isEditMode = ref(false)
const isSaving = ref(false)
const form = ref<MDBSite>(emptyForm())
// Sert de clé de route pour le PUT : le site peut être renommé (lettresDappel modifié) dans le formulaire
const originalLettresDappel = ref('')

function openCreate() {
  form.value = emptyForm()
  originalLettresDappel.value = ''
  isEditMode.value = false
  showDialog.value = true
}

function openEdit(site: MDBSite) {
  form.value = { ...site }
  originalLettresDappel.value = site.lettresDappel
  isEditMode.value = true
  showDialog.value = true
}

async function saveDialog() {
  if (!form.value.nom.trim() || !form.value.lettresDappel.trim()) {
    notifyError('Le nom et les lettres d\'appel sont requis.')
    return
  }

  isSaving.value = true
  try {
    const payload: MDBSite = {
      ...form.value,
      id: form.value.id || emptyMongoId(),
      nom: form.value.nom.trim(),
      lettresDappel: form.value.lettresDappel.trim(),
      lettresDappelPrisme: form.value.lettresDappelPrisme.trim(),
      type: form.value.type.trim(),
      siteGroupeLD: form.value.siteGroupeLD.trim(),
      url: form.value.url.trim(),
    }

    if (isEditMode.value) {
      await apiProphet.put(`sites/${originalLettresDappel.value}`, payload)
      Notify.success('Site modifié avec succès.')
    } else {
      await apiProphet.post('sites', { ...payload, id: '' })
      Notify.success('Site créé avec succès.')
    }

    showDialog.value = false
    await getData()
  } catch (e) {
    notifyError(e)
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div class="pa-4 pa-sm-6">
    <!-- Toolbar -->
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <v-text-field
        v-model="filter"
        label="Rechercher"
        density="compact"
        variant="outlined"
        rounded="lg"
        hide-details
        prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
        clearable
        style="max-width: 260px;"/>

      <v-spacer/>

      <v-btn
        color="primary"
        variant="tonal"
        rounded="pill"
        prepend-icon="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"
        :disabled="isLoading"
        @click="openCreate">
        Nouveau site
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredSites"
      :loading="isLoading"
      :hide-default-header="display.smAndDown.value"
      item-value="id"
      density="comfortable"
      hover>

      <!-- Mobile card rows -->
      <template v-if="display.smAndDown.value" #item="{ item }">
        <div class="site-mobile-card ma-2 pa-3">
          <div class="d-flex align-center justify-space-between mb-2">
            <div style="min-width: 0;">
              <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                <span class="text-subtitle-2 font-weight-bold text-truncate">{{ item.lettresDappel }}</span>
                <v-chip v-if="item.estMembrePrisme" color="info" size="x-small" variant="tonal">Prisme</v-chip>
              </div>
              <div class="text-caption text-medium-emphasis text-truncate">{{ item.nom || '—' }}</div>
            </div>
            <v-btn icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" variant="tonal" color="primary" size="small" class="flex-shrink-0 ms-2" @click="openEdit(item)"/>
          </div>
          <v-divider class="mb-2"/>
          <div class="d-flex flex-wrap mt-1" style="gap: 8px;">
            <span class="text-caption">Type: {{ item.type || '—' }}</span>
            <span class="text-caption">Groupe: {{ item.siteGroupeLD || '—' }}</span>
          </div>
        </div>
      </template>

      <template #item.estMembrePrisme="{ item }">
        <v-icon v-if="item.estMembrePrisme" icon="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z" color="success" size="18"/>
        <v-icon v-else icon="M19,13H5V11H19V13Z" color="grey" size="18"/>
      </template>

      <template #item.actions="{ item }">
        <v-btn icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" variant="text" size="small" color="primary" @click="openEdit(item)"/>
      </template>

      <template #no-data>
        <div class="text-center py-8 text-medium-emphasis">
          <v-icon icon="M18,15H16V17H18M18,11H16V13H18M20,19H12V17H14V15H12V13H14V11H12V9H20M10,7H8V5H10M10,11H8V9H10M10,15H8V13H10M10,19H8V17H10M6,7H4V5H6M6,11H4V9H6M6,15H4V13H6M6,19H4V17H6M12,7V3H2V21H22V7H12Z" size="40" class="mb-2"/>
          <div>Aucun site trouvé</div>
        </div>
      </template>
    </v-data-table>

    <!-- ── Create / Edit dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="showDialog" :fullscreen="display.smAndDown.value" :max-width="display.smAndDown.value ? undefined : 640" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center pa-4" style="gap: 10px;">
          <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
            <v-icon :icon="isEditMode ? 'M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z' : 'M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z'" size="20"/>
          </v-avatar>
          <span class="text-h6">{{ isEditMode ? 'Modifier le site' : 'Ajouter un site' }}</span>
          <v-spacer/>
          <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="showDialog = false"/>
        </v-card-title>

        <v-divider/>

        <v-card-text class="pa-4">
          <v-container class="pa-0">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.nom" label="Nom *" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M18,15H16V17H18M18,11H16V13H18M20,19H12V17H14V15H12V13H14V11H12V9H20M10,7H8V5H10M10,11H8V9H10M10,15H8V13H10M10,19H8V17H10M6,7H4V5H6M6,11H4V9H6M6,15H4V13H6M6,19H4V17H6M12,7V3H2V21H22V7H12Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.lettresDappel" label="Lettres d'appel *" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M3,5A2,2 0 0,1 5,3H19A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5M5,5V19H19V5H5M11,7H13A2,2 0 0,1 15,9V10H13V9H11V15H13V14H15V15A2,2 0 0,1 13,17H11A2,2 0 0,1 9,15V9A2,2 0 0,1 11,7Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.lettresDappelPrisme" label="Lettres d'appel Prisme" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M9,7H13A2,2 0 0,1 15,9V11A2,2 0 0,1 13,13H11V17H9V7M11,9V11H13V9H11M5,3H19A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3M5,5V19H19V5H5Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.type" label="Type" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M11,13.5V21.5H3V13.5H11M9,15.5H5V19.5H9V15.5M12,2L17.5,11H6.5L12,2M12,5.86L10.08,9H13.92L12,5.86M17.5,13C20,13 22,15 22,17.5C22,20 20,22 17.5,22C15,22 13,20 13,17.5C13,15 15,13 17.5,13M17.5,15A2.5,2.5 0 0,0 15,17.5A2.5,2.5 0 0,0 17.5,20A2.5,2.5 0 0,0 20,17.5A2.5,2.5 0 0,0 17.5,15Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.siteGroupeLD" label="Groupe (lettres d'appel)" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M1,1V5H2V19H1V23H5V22H19V23H23V19H22V5H23V1H19V2H5V1M5,4H19V5H20V19H19V20H5V19H4V5H5M6,6V14H9V18H18V9H14V6M8,8H12V12H8M14,11H16V16H11V14H14" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6" class="d-flex align-center">
                <v-switch v-model="form.estMembrePrisme" color="info" label="Membre Prisme" hide-details density="compact"/>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.url" label="URL" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M10.59,13.41C11,13.8 11,14.44 10.59,14.83C10.2,15.22 9.56,15.22 9.17,14.83C7.22,12.88 7.22,9.71 9.17,7.76V7.76L12.71,4.22C14.66,2.27 17.83,2.27 19.78,4.22C21.73,6.17 21.73,9.34 19.78,11.29L18.29,12.78C18.3,11.96 18.17,11.14 17.89,10.36L18.36,9.88C19.54,8.71 19.54,6.81 18.36,5.64C17.19,4.46 15.29,4.46 14.12,5.64L10.59,9.17C9.41,10.34 9.41,12.24 10.59,13.41M13.41,9.17C13.8,8.78 14.44,8.78 14.83,9.17C16.78,11.12 16.78,14.29 14.83,16.24V16.24L11.29,19.78C9.34,21.73 6.17,21.73 4.22,19.78C2.27,17.83 2.27,14.66 4.22,12.71L5.71,11.22C5.7,12.04 5.83,12.86 6.11,13.65L5.64,14.12C4.46,15.29 4.46,17.19 5.64,18.36C6.81,19.54 8.71,19.54 9.88,18.36L13.41,14.83C14.59,13.66 14.59,11.76 13.41,10.59C13,10.2 13,9.56 13.41,9.17Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.latitudeGoogleMaps" label="Latitude (Google Maps)" type="number" variant="outlined"
                  density="compact" rounded="lg" prepend-inner-icon="M12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5M12,2A7,7 0 0,1 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9A7,7 0 0,1 12,2M12,4A5,5 0 0,0 7,9C7,10 7,12 12,18.71C17,12 17,10 17,9A5,5 0 0,0 12,4Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.longitudeGoogleMaps" label="Longitude (Google Maps)" type="number" variant="outlined"
                  density="compact" rounded="lg" prepend-inner-icon="M12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5M12,2A7,7 0 0,1 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9A7,7 0 0,1 12,2M12,4A5,5 0 0,0 7,9C7,10 7,12 12,18.71C17,12 17,10 17,9A5,5 0 0,0 12,4Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.latitudeSourceParDéfaut" label="Latitude source par défaut" type="number" variant="outlined"
                  density="compact" rounded="lg" prepend-inner-icon="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.longitudeSourceParDéfaut" label="Longitude source par défaut" type="number" variant="outlined"
                  density="compact" rounded="lg" prepend-inner-icon="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z" hide-details="auto"/>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider/>

        <v-card-actions class="pa-4">
          <v-spacer/>
          <v-btn variant="text" @click="showDialog = false">Annuler</v-btn>
          <v-btn color="primary" variant="flat" rounded="pill" prepend-icon="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z" :loading="isSaving" @click="saveDialog">
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.site-mobile-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}
</style>
